import { useMemo, useState } from "react";
import { Briefcase, X } from "lucide-react";
import { useJobPostings } from "../hooks/queries/useJobPostings";
import JobCard from "../apps/candidate/components/jobs/JobCard";
import JobSearchBar from "../apps/candidate/components/jobs/JobSearchBar";
import JobFilters from "../apps/candidate/components/jobs/JobFilters";

interface JobBrowsePageProps {
  onSelectJob?: (jobId: string) => void;
  onBack?: () => void;
}

export default function JobBrowsePage({ onSelectJob, onBack }: JobBrowsePageProps) {
  const [search, setSearch] = useState("");
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);

  const { data: jobs, isLoading, isError } = useJobPostings({ status: "active" });

  const list = jobs ?? [];

  // Extract unique locations from jobs data
  const locations = useMemo(() => {
    const seen = new Set<string>();
    for (const job of list) {
      const loc = job.locationName;
      if (loc) seen.add(loc);
    }
    return Array.from(seen).sort();
  }, [list]);

  // Client-side filtering
  const filtered = useMemo(() => {
    let result = list;

    if (selectedLocation) {
      result = result.filter((job) => job.locationName === selectedLocation);
    }

    if (search.trim()) {
      const lower = search.toLowerCase();
      result = result.filter(
        (job) =>
          job.title.toLowerCase().includes(lower) ||
          job.companyName.toLowerCase().includes(lower),
      );
    }

    return result;
  }, [list, search, selectedLocation]);

  const hasActiveFilters = Boolean(search.trim() || selectedLocation);

  const clearFilters = () => {
    setSearch("");
    setSelectedLocation(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-5">
        <div className="flex items-center justify-between">
          <div>
            <h1
              className="text-2xl font-extrabold text-gray-900"
              style={{ fontFamily: "Nunito, sans-serif" }}
            >
              Browse Jobs
            </h1>
            {!isLoading && !isError && (
              <p className="text-sm text-gray-500 mt-1">
                {filtered.length} open position{filtered.length !== 1 ? "s" : ""}
              </p>
            )}
          </div>
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              My Applications
            </button>
          )}
        </div>
      </header>

      {/* Search + Filters */}
      <div className="px-4 pt-5 pb-3 max-w-2xl mx-auto space-y-3">
        <JobSearchBar value={search} onChange={setSearch} />
        <JobFilters
          locations={locations}
          selectedLocation={selectedLocation}
          onSelectLocation={setSelectedLocation}
        />
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <div className="w-10 h-10 border-4 border-[#0156FF] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-base text-gray-500">Loading jobs…</p>
          </div>
        </div>
      ) : isError ? (
        <div className="flex items-center justify-center py-24 px-6">
          <div className="text-center">
            <p className="text-xl font-bold text-gray-900 mb-2">Unable to Load Jobs</p>
            <p className="text-base text-gray-500">Please try again later.</p>
          </div>
        </div>
      ) : (
        <div className="px-4 py-4 space-y-3 max-w-2xl mx-auto">
          {filtered.length === 0 ? (
            <div className="text-center py-16">
              <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-lg font-semibold text-gray-700 mb-2">
                {hasActiveFilters ? "No jobs match your filters" : "No open positions right now"}
              </p>
              <p className="text-sm text-gray-500 mb-5">
                {hasActiveFilters
                  ? "Try adjusting your search or location filter."
                  : "Check back soon for new opportunities."}
              </p>
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
                >
                  <X className="w-4 h-4" />
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            filtered.map((job) => (
              <JobCard
                key={job.jobId}
                job={job}
                onSelect={(jobId) => {
                  if (onSelectJob) {
                    onSelectJob(jobId);
                  } else {
                    window.location.href = `/apply?jobId=${jobId}`;
                  }
                }}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}
